import React from "react";
import {
  Box,
  Paper,
  Typography,
  Popover,
  Grid,
} from "@mui/material";

const AppSwitcher = ({ anchorEl, onClose, isDarkTheme }) => {
  const open = Boolean(anchorEl);

  const handleAppClick = (url) => {
    window.open(url, '_blank');
    onClose();
  };

  // Available apps configuration
  const availableApps = [
    {
      name: "Orucore",
      logo: "/OCore.png",
      url: "https://orucore.com",
      color: "#4285f4",
      description: "CRM Tool"
    },
    {
      name: "Oruclicks",
      logo: "/OC.png",
      url: "https://oruclicks.com",
      color: "#ea4335",
      description: "Automatic Marketing Tool"
    },
    {
      name: "Orunal",
      logo: "/ON.png",
      url: "https://orunal.com",
      color: "#34a853",
      description: "Task Management Tool"
    },
  ];

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      PaperProps={{
        sx: {
          mt: 1.5,
          borderRadius: "16px",
          background: isDarkTheme 
            ? "linear-gradient(145deg, #1e1e1e 0%, #2d2d2d 50%, #1a1a1a 100%)" 
            : "linear-gradient(145deg, #ffffff 0%, #f8f9fa 50%, #e8eaf6 100%)",
          boxShadow: isDarkTheme
            ? "0 16px 48px rgba(0, 0, 0, 0.7), 0 0 1px rgba(255,255,255,0.1) inset"
            : "0 16px 48px rgba(0, 0, 0, 0.2), 0 0 1px rgba(0,0,0,0.05) inset",
          padding: "16px",
          minWidth: "340px",
          maxWidth: "360px",
          border: isDarkTheme 
            ? "1px solid rgba(255,255,255,0.15)" 
            : "1px solid rgba(63, 81, 181, 0.15)",
          backdropFilter: "blur(20px)",
        },
      }}
    >
      <Box 
        sx={{ 
          mb: 1.5,
          pb: 1.25,
          borderBottom: isDarkTheme 
            ? "2px solid rgba(255,255,255,0.08)" 
            : "2px solid rgba(63, 81, 181, 0.1)",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: isDarkTheme ? "#fff" : "#1a237e",
            fontWeight: "800",
            fontSize: "16px",
            mb: 0.25,
            letterSpacing: "-0.3px",
            background: isDarkTheme 
              ? "linear-gradient(90deg, #fff 0%, #90caf9 100%)"
              : "linear-gradient(90deg, #1a237e 0%, #3f51b5 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Switch Apps
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: isDarkTheme ? "#aaa" : "#5c6bc0",
            fontSize: "11px",
            fontWeight: "500",
          }}
        >
          Choose your platform
        </Typography>
      </Box>
      
      <Grid container spacing={1.25}>
        {availableApps.map((app, index) => (
          <Grid item xs={6} key={index}>
            <Paper
              elevation={0}
              onClick={() => handleAppClick(app.url)}
              sx={{
                  padding: "12px 10px",
                  borderRadius: "14px",
                  cursor: "pointer",
                  textAlign: "center",
                  background: isDarkTheme 
                    ? "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)" 
                    : "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,249,250,0.8) 100%)",
                  border: isDarkTheme 
                    ? `2px solid rgba(255,255,255,0.08)` 
                    : `2px solid ${app.color}15`,
                  transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: isDarkTheme
                    ? "0 3px 10px rgba(0,0,0,0.3)"
                    : "0 3px 10px rgba(0,0,0,0.08)",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(135deg, ${app.color}20, ${app.color}05)`,
                  opacity: 0,
                  transition: "opacity 0.4s ease",
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: "-50%",
                  left: "-50%",
                  width: "200%",
                  height: "200%",
                  background: `radial-gradient(circle, ${app.color}15 0%, transparent 70%)`,
                  opacity: 0,
                  transition: "opacity 0.4s ease",
                },
                "&:hover": {
                  transform: "translateY(-4px) scale(1.02)",
                  background: isDarkTheme 
                    ? `linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.05) 100%)` 
                    : `linear-gradient(135deg, #ffffff 0%, ${app.color}08 100%)`,
                  borderColor: app.color,
                  boxShadow: isDarkTheme 
                    ? `0 8px 24px rgba(0,0,0,0.5), 0 0 0 2px ${app.color}30, inset 0 1px 0 rgba(255,255,255,0.1)` 
                    : `0 8px 24px ${app.color}30, 0 0 0 2px ${app.color}20, inset 0 1px 0 rgba(255,255,255,0.8)`,
                  "&::before": {
                    opacity: 1,
                  },
                  "&::after": {
                    opacity: 1,
                  },
                  "& .icon-container": {
                    transform: "scale(1.08) rotate(3deg)",
                    boxShadow: `0 6px 16px ${app.color}40`,
                  },
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mb: 1,
                  position: "relative",
                  zIndex: 2,
                }}
              >
                <Box
                  className="icon-container"
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: isDarkTheme
                      ? `linear-gradient(135deg, ${app.color}25, ${app.color}15)`
                      : `linear-gradient(135deg, ${app.color}20, ${app.color}10)`,
                    transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
                    boxShadow: isDarkTheme
                      ? `0 4px 16px ${app.color}20, inset 0 1px 0 rgba(255,255,255,0.1)`
                      : `0 4px 16px ${app.color}15, inset 0 1px 0 rgba(255,255,255,0.5)`,
                    border: isDarkTheme
                      ? `1px solid ${app.color}30`
                      : `1px solid ${app.color}25`,
                    padding: "10px",
                    overflow: "hidden",
                  }}
                >
                  <img 
                    src={app.logo} 
                    alt={app.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                    }}
                  />
                </Box>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "700",
                  fontSize: "13px",
                  color: isDarkTheme ? "#fff" : "#1a237e",
                  position: "relative",
                  zIndex: 2,
                  letterSpacing: "-0.2px",
                }}
              >
                {app.name}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
      
      <Box
        sx={{
          mt: 1.5,
          pt: 1.25,
          borderTop: isDarkTheme 
            ? "2px solid rgba(255,255,255,0.08)" 
            : "2px solid rgba(63, 81, 181, 0.1)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.75,
            background: isDarkTheme
              ? "rgba(76, 175, 80, 0.1)"
              : "rgba(76, 175, 80, 0.08)",
            padding: "6px 12px",
            borderRadius: "10px",
            border: isDarkTheme
              ? "1px solid rgba(76, 175, 80, 0.2)"
              : "1px solid rgba(76, 175, 80, 0.15)",
          }}
        >
          <Box
            component="span"
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: isDarkTheme ? "#4caf50" : "#388e3c",
              boxShadow: `0 0 8px ${isDarkTheme ? "#4caf50" : "#388e3c"}`,
              animation: "pulse 2s infinite",
              "@keyframes pulse": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0.5 },
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: isDarkTheme ? "#4caf50" : "#2e7d32",
              fontSize: "10px",
              fontWeight: "600",
              letterSpacing: "0.2px",
            }}
          >
            Opens in new tab
          </Typography>
        </Box>
      </Box>
    </Popover>
  );
};

export default AppSwitcher;
